import { RefetchOptions, QueryClient, RefetchQueryFilters } from "@tanstack/react-query";
import { ParamsForMethod, QueryDataForMethod, QueryMethodForModel, ModelName, TellescopeQueryKey } from "../../types";
import { TellescopeRefetchQueryFilters } from "../../hooks";
import { refetchTellescopeQueries } from "./refetch-tellescope-queries";

/**
 * Arguments for refetching all queries for a specific Tellescope model.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 */
export interface RefetchTellescopeModelQueriesArgs<Model extends ModelName, TError = Error> {
  /** The Tellescope model to refetch queries for */
  model: Model;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /**
   * Optional filters applied to all queries for this model.
   * These filters are combined with method-specific filters if both are provided.
   */
  filters?: Omit<
    TellescopeRefetchQueryFilters<
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TError,
      QueryDataForMethod<Model, QueryMethodForModel<Model>>,
      TellescopeQueryKey<Model, QueryMethodForModel<Model>>
    >,
    "queryKey"
  >;
  /** Optional refetch options (e.g., throwOnError, cancelRefetch) applied to all refetches */
  options?: RefetchOptions;
  /**
   * Optional method-specific filters. Allows refetching queries for specific methods
   * and optionally matching by request parameters.
   */
  methodFilters?: Partial<{
    [Method in QueryMethodForModel<Model>]: Omit<
      TellescopeRefetchQueryFilters<
        QueryDataForMethod<Model, Method>,
        TError,
        QueryDataForMethod<Model, Method>,
        TellescopeQueryKey<Model, Method>
      >,
      "queryKey"
    > & {
      /** Optional request parameters to match for this method */
      request?: Partial<ParamsForMethod<Model, Method>>;
      /** Optional method-specific refetch options that override global options */
      options?: RefetchOptions;
      requestKeysToOmitFromQueryKey?: (keyof ParamsForMethod<Model, Method>)[];
    };
  }>;
}

/**
 * Refetches all cached queries for a specific Tellescope model.
 *
 * This function provides a way to refetch all queries for a model, optionally
 * with method-specific filters. When no methodFilters are provided, it refetches
 * all queries for the model. When methodFilters are provided, it only refetches
 * queries for the specified methods.
 *
 * @template Model - The Tellescope model name (must support query methods)
 * @template TError - The error type for the queries (defaults to Error)
 *
 * @param args - Configuration object containing model, queryClient, and optional filters
 * @returns A promise that resolves when refetching is complete
 *
 * @example
 * ```ts
 * // Refetch all user queries
 * await refetchTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 * });
 * ```
 *
 * @example
 * ```ts
 * // Refetch only specific methods
 * await refetchTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   methodFilters: {
 *     getOne: {
 *       // Refetch all getOne queries
 *     },
 *     getSome: {
 *       request: { filter: { fname: "John" }, limit: 10 },
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use global filters with method-specific filters
 * await refetchTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   filters: {
 *     stale: true, // Only refetch stale queries
 *   },
 *   methodFilters: {
 *     getOne: {
 *       // Inherits stale: true from global filters
 *     },
 *   },
 * });
 * ```
 *
 * @example
 * ```ts
 * // Use global and method-specific options
 * await refetchTellescopeModelQueries({
 *   queryClient,
 *   model: "users",
 *   options: {
 *     throwOnError: true, // Global option for all methods
 *   },
 *   methodFilters: {
 *     getOne: {
 *       options: {
 *         cancelRefetch: false, // Method-specific option (overrides global)
 *       },
 *     },
 *   },
 * });
 * ```
 */
export const refetchTellescopeModelQueries = async <Model extends ModelName, TError = Error>({
  model,
  queryClient,
  filters,
  methodFilters,
  options,
}: RefetchTellescopeModelQueriesArgs<Model, TError>) => {
  const methodNames = Object.keys(methodFilters ?? {}) as QueryMethodForModel<Model>[];

  // If no method filters are specified, refetch all queries for the model
  if (!methodFilters || !methodNames.length) {
    return queryClient.refetchQueries(
      {
        queryKey: [model],
        exact: false,
        ...filters,
      } as RefetchQueryFilters<Model[]>,
      options
    );
  }

  // Otherwise, refetch queries for each specified method
  const refetchPromises = methodNames.map(<Method extends QueryMethodForModel<Model>>(method: Method) => {
    const { request, options: methodOptions, ...methodSpecificFilters } = methodFilters[method] ?? {};

    return refetchTellescopeQueries<Model, Method, TError>({
      model,
      method,
      request,
      queryClient,
      filters: {
        ...filters,
        ...methodSpecificFilters,
      },
      options: { ...options, ...methodOptions },
    });
  });

  await Promise.all(refetchPromises);
};
